import type { CSSProperties } from 'react';
import {
  resolveSectionColor,
  sectionSurfaceCssVars,
  sectionSurfaceTokens,
  withAlpha,
  type SectionSurfaceTokens,
} from './sectionColors';

export {
  resolveSectionColor,
  sectionSurfaceCssVars,
  sectionSurfaceTokens,
  withAlpha,
};
export type { SectionSurfaceTokens };

/** Inline style object for a category/section bordered surface. */
export function sectionSurfaceStyle(
  categoryOrHex: string,
  options?: { selected?: boolean; interactive?: boolean }
): CSSProperties {
  const hex = categoryOrHex.startsWith('#')
    ? categoryOrHex
    : resolveSectionColor(categoryOrHex);
  const t = sectionSurfaceTokens(hex);
  const vars = sectionSurfaceCssVars(hex) as CSSProperties;
  return {
    ...vars,
    borderColor: options?.selected ? t.borderActive : t.border,
    backgroundColor: options?.selected ? t.tintHover : t.tint,
  };
}

/** Class list for section surfaces (pair with sectionSurfaceStyle / CSS vars). */
export function sectionSurfaceClassName(options?: {
  interactive?: boolean;
  selected?: boolean;
  className?: string;
}): string {
  const parts = ['section-surface'];
  if (options?.interactive) parts.push('section-surface-interactive');
  if (options?.selected) parts.push('section-surface-selected');
  if (options?.className) parts.push(options.className);
  return parts.join(' ');
}

/**
 * Category-tinted column chrome for Explore / Car Show Mode list + detail panes.
 * Matches the bordered-system language (no brand-orange defaults).
 */
export function categoryColumnStyle(
  categoryOrHex: string | null | undefined,
  variant: 'list' | 'detail'
): CSSProperties | undefined {
  if (!categoryOrHex) return undefined;
  const hex = categoryOrHex.startsWith('#')
    ? categoryOrHex
    : resolveSectionColor(categoryOrHex);
  if (variant === 'list') {
    return {
      borderColor: withAlpha(hex, 0.38),
      background: `linear-gradient(180deg, ${withAlpha(hex, 0.12)} 0%, ${withAlpha(hex, 0.045)} 42%, rgba(10, 10, 10, 0.55) 100%)`,
      boxShadow: `inset 0 0 0 1px ${withAlpha(hex, 0.07)}`,
    };
  }
  return {
    borderColor: withAlpha(hex, 0.3),
    background: `linear-gradient(165deg, ${withAlpha(hex, 0.14)} 0%, ${withAlpha(hex, 0.05)} 36%, rgba(14, 14, 14, 0.92) 72%, rgba(10, 10, 10, 0.95) 100%)`,
    boxShadow: `inset 0 1px 0 ${withAlpha(hex, 0.14)}`,
  };
}
